import { Button, Card, Flex, FormStatus, Lead, P, Skeleton } from '@dnb/eufemia';
import { Form, Iterate } from '@dnb/eufemia/extensions/forms';
import { add as AddIcon } from '@dnb/eufemia/icons';
import type { ClientJwksDto } from '@portals/shared/portal/JwksDto';
import { type JSX, useCallback, useMemo, useState } from 'react';
import useSWR from 'swr';

import { getClientKeys } from '@/api/app';
import Toast from '@/components/Toast';

import { getTooltipMessage } from '../../../../../Credential/components/ClientCard';
import AddKeyForm from './AddKeyForm';
import KeyRow from './KeyRow';

// Rendered inside a Skeleton provider, so these paragraphs pick the skeleton up
// from context and become bars. A custom `figure` would not - Skeleton only
// mounts its provider when no figure is given - so the text would show through.
const loadingFigure = (
  <Flex.Stack gap="small" wrap={false}>
    <P size="small">Loading keys</P>
    <Card innerSpace="small" stack>
      <P size="small">Key ID 0000000000000000000000000000000000000000000</P>
      <P size="small">Type EC</P>
      <P size="small">Curve P-256</P>
    </Card>
  </Flex.Stack>
);

type Props = {
  appId: string;
  clientId: string;
  liveMode: boolean;
  isAdmin: boolean;
  hasClientManagementGrp: boolean;
  hasScopeEntitlements: boolean;
  hasPrivilegeToEditClient: boolean;
};

export default function SigningKeysCard({
  appId,
  clientId,
  liveMode,
  isAdmin,
  hasClientManagementGrp,
  hasScopeEntitlements,
  hasPrivilegeToEditClient,
}: Props): JSX.Element {
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [addingKey, setAddingKey] = useState(false);

  const {
    data,
    isLoading,
    error,
    mutate: mutateKeys,
  } = useSWR<ClientJwksDto>(`/apps/${appId}/clients/${clientId}/jwks`, () => getClientKeys(appId, clientId));

  const keys = useMemo(() => data?.keys ?? [], [data]);

  const distinctKidCount = useMemo(() => new Set(keys.map(({ kid }) => kid)).size, [keys]);

  const noPrivilegeError = useMemo(
    () => getTooltipMessage(isAdmin, hasClientManagementGrp, hasScopeEntitlements, 'update'),
    [isAdmin, hasClientManagementGrp, hasScopeEntitlements],
  );

  const refresh = useCallback(
    async (message: string) => {
      await mutateKeys();
      setSuccessMessage(message);
      setTimeout(() => setSuccessMessage(null), 5000);
    },
    [mutateKeys],
  );

  const onKeyAdded = useCallback(async () => {
    setAddingKey(false);
    await refresh('Signing key added');
  }, [refresh]);
  const onKeyRemoved = useCallback(() => refresh('Signing key removed'), [refresh]);

  const openAddKeyForm = useCallback(() => setAddingKey(true), []);
  const closeAddKeyForm = useCallback(() => setAddingKey(false), []);

  return (
    <Form.Card innerSpace="small" outline="var(--color-black-8)" outset={false}>
      <Lead size="basis">Signing keys</Lead>
      <Flex.Stack gap="medium" wrap={false}>
        <P>
          The public keys CIAM uses to verify tokens signed by this client. Keys cannot be edited, only added or
          removed, and <strong>changes can take a few minutes to take effect</strong>.
        </P>
        {successMessage && <Toast message={successMessage} position="bottom" variant="success" />}
        {error ? (
          <FormStatus state="error">Could not load the signing keys for this client.</FormStatus>
        ) : isLoading ? (
          <Skeleton show>{loadingFigure}</Skeleton>
        ) : keys.length === 0 ? (
          <P>No keys registered</P>
        ) : (
          <Flex.Stack gap="small" wrap={false}>
            <P size="small">
              {distinctKidCount} {distinctKidCount === 1 ? 'key' : 'keys'} registered
            </P>
            <Iterate.Array animate containerMode="view" value={keys} wrap={false}>
              <KeyRow
                appId={appId}
                canManageKeys={hasPrivilegeToEditClient}
                clientId={clientId}
                distinctKidCount={distinctKidCount}
                isAdmin={isAdmin}
                liveMode={liveMode}
                noPrivilegeError={noPrivilegeError}
                onKeyRemoved={onKeyRemoved}
              />
            </Iterate.Array>
          </Flex.Stack>
        )}
        <Form.Visibility animate visible={addingKey}>
          <AddKeyForm
            appId={appId}
            canManageKeys={hasPrivilegeToEditClient}
            clientId={clientId}
            isAdmin={isAdmin}
            liveMode={liveMode}
            noPrivilegeError={noPrivilegeError}
            onCancel={closeAddKeyForm}
            onKeyAdded={onKeyAdded}
          />
        </Form.Visibility>
        <Form.Visibility animate visible={!addingKey}>
          <Form.Section.Toolbar>
            <Button
              disabled={!hasPrivilegeToEditClient}
              icon={AddIcon}
              iconPosition="left"
              onClick={openAddKeyForm}
              text="Add key"
              tooltip={hasPrivilegeToEditClient ? undefined : noPrivilegeError}
              variant="tertiary"
            />
          </Form.Section.Toolbar>
        </Form.Visibility>
      </Flex.Stack>
    </Form.Card>
  );
}
